/**
 * `NumberTween` — a numeric value that interpolates to its new value
 * (short count-up, `MOTION.durBase`, requestAnimationFrame) instead
 * of jumping. `tabular-nums` keeps the width stable while digits
 * roll.
 *
 * Used for: KPI values and delta chips after a refetch (plan §2.2).
 *
 * Non-numeric string values render as-is WITHOUT animating. This is a
 * documented passthrough for already-formatted display values
 * ("$2.4M", "—") — NOT a data fallback: nothing is invented, the
 * exact value received is what renders.
 *
 * Two phases, mirroring `useCountUp` (bi-components) — its twin for
 * labels that live inside that package:
 *   - ENTRANCE (mount-once): `delayMs` hold + `durationMs` ease-out.
 *     Call sites pass `MOTION.delayEntrance`/`MOTION.durEntrance` so
 *     numbers land on the same beat as the CSS entrances.
 *   - LATER CHANGES (a filter refetch): MORPH from the value on screen
 *     to the new one over `MOTION.durBase`, with NO delay. Holding the
 *     PREVIOUS number for 250ms while new data is already in hand shows
 *     stale data with no signal — the contract forbids it.
 *
 * Decimal consistency (review #56 #9): INTERMEDIATE frames are
 * quantized to the target's decimal count (capped, `decimalsOf`) so an
 * integer target never paints fractions and the formatted width does
 * not dance mid-count.
 *
 * LANDED frame = the exact value, never a rounded one (BI hard rule,
 * motion audit hallazgo 2): once `display` reaches the target, the
 * prop's `value` is formatted AS RECEIVED — no `toFixed`, no capped
 * `Intl` — so the settled frame is byte-identical to what the static
 * (non-animated) path renders. This is also the frame that
 * reduced-motion users, hidden tabs and ghost snapshots see, i.e. the
 * only frame for them. Rounding it turned "0.00123" into 0.0012 and
 * "0.0000123" into a flat 0 — a wrong number, not a transient one.
 *
 * Reduced motion: the new value renders immediately.
 */
import { useEffect, useMemo, useRef, useState } from "react";

import {
  MOTION,
  useEntranceSuppressed,
  usePrefersReducedMotion,
} from "@pervasivemind/bi-components";

export interface NumberTweenProps {
  /** Numeric → tweened; string → rendered directly (passthrough). */
  value: number | string;
  /** ENTRANCE duration in ms (the first animation only). Defaults to
   *  `MOTION.durBase`. Later value changes always morph over
   *  `MOTION.durBase` — the refetch language, not the entrance one. */
  durationMs?: number;
  /**
   * Formats each animation frame's value. Intermediate frames get the
   * quantized value; the LANDED frame gets `value` exactly as received
   * (byte-identical guarantee — e.g. `parseFormattedNumber`'s `render`
   * returns the original string literally). Defaults to a grouped
   * locale format that keeps every digit of the value.
   */
  format?: (n: number) => string;
  className?: string;
  /** Entrance (ronda 12): start the FIRST paint at 0 and tween up to
   *  `value` on mount. Later value changes morph from the value on
   *  screen. Reduced motion renders the final value immediately
   *  (built-in). */
  fromZero?: boolean;
  /** ENTRANCE hold before the count starts (ronda 13: mirrors
   *  `MOTION.delayEntrance` so number count-ups start on the same beat
   *  as the CSS entrances). Applies to the first animation ONLY — a
   *  refetch morph starts immediately, never parking on the previous
   *  number. Reduced-motion/hidden paths ignore it (final value
   *  immediately). */
  delayMs?: number;
}

/** Decimal count for INTERMEDIATE frames — capped so a long-tailed
 *  target does not make every frame jitter. The landed frame does NOT
 *  go through this (it renders `value` exactly). */
function decimalsOf(n: number): number {
  if (Number.isInteger(n)) return 0;
  const s = String(n);
  const dot = s.indexOf(".");
  return dot === -1 ? 0 : Math.min(s.length - dot - 1, 4);
}

/** `Intl`'s ceiling for fraction digits; a magnitude below 1e-20 cannot
 *  be expressed with it and would format as a flat "0". */
const EXACT_MAX_FRACTION_DIGITS = 20;

let exactNumberFormat: Intl.NumberFormat | null = null;

/**
 * Default formatter for the LANDED frame: grouped locale format that
 * preserves every digit of the value's canonical representation
 * (`maximumFractionDigits: 20` reproduces `String(n)`'s shortest
 * round-trip form — verified for 3.14159, 0.0000123, 1e-7, 1/3).
 * Sub-1e-20 magnitudes fall outside `Intl`'s range and would print as
 * "0"; those render their canonical string instead — a nonzero value
 * must never be shown as zero (BI rule). Nothing is invented here: the
 * exact received value is what renders.
 */
function formatExact(n: number): string {
  if (n !== 0 && Math.abs(n) < 1e-20) return String(n);
  if (exactNumberFormat === null) {
    exactNumberFormat = new Intl.NumberFormat(undefined, {
      maximumFractionDigits: EXACT_MAX_FRACTION_DIGITS,
    });
  }
  return exactNumberFormat.format(n);
}

/** Decelerating cubic — the rAF-space analog of `MOTION.easeOut`. */
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

export function NumberTween({
  value,
  durationMs = MOTION.durBase,
  format,
  className,
  fromZero = false,
  delayMs = 0,
}: NumberTweenProps): JSX.Element {
  const reduced = usePrefersReducedMotion();
  // Inside a FadeSwap ghost the whole subtree is a settled snapshot:
  // no fromZero restart, no tween — paint the value directly.
  const suppressed = useEntranceSuppressed();
  const numeric = typeof value === "number" && Number.isFinite(value);

  // The value currently painted; the tween moves it toward `value`.
  // `fromZero` seeds the first paint at 0 so the mount effect tweens
  // 0 -> value (entrance); otherwise mount paints the value directly.
  const [display, setDisplay] = useState<number>(
    numeric ? (fromZero && !suppressed ? 0 : (value as number)) : 0,
  );
  const displayRef = useRef(display);
  displayRef.current = display;
  const rafRef = useRef<number | null>(null);
  // Entrance bookkeeping (motion audit hallazgo 1). Keyed by the FIRST
  // numeric target rather than a plain "already ran" flag so that
  // StrictMode's double-invoked mount effect still counts as the
  // entrance (same target ⇒ same phase); the entrance is over for good
  // as soon as a DIFFERENT value arrives.
  const entranceTargetRef = useRef<number | null>(null);
  const entranceOverRef = useRef(false);

  useEffect(() => {
    if (!numeric) return undefined;
    const target = value as number;
    // Phase: the entrance owns `durationMs`/`delayMs`; every later
    // change morphs over `MOTION.durBase` with NO hold — parking on the
    // previous number for `delayEntrance` while the new data is already
    // in hand paints stale data with no signal (contract), and its twin
    // `useCountUp` already splits the two phases this way.
    if (entranceTargetRef.current === null) entranceTargetRef.current = target;
    const isEntrance = !entranceOverRef.current && entranceTargetRef.current === target;
    if (!isEntrance) entranceOverRef.current = true;
    const durMs = isEntrance ? durationMs : MOTION.durBase;
    const holdMs = isEntrance ? delayMs : 0;
    // Occluded-document guard (ronda 12 fix): Chrome pauses rAF in
    // hidden/occluded tabs, which froze tweens MID-FLIGHT (a 42%
    // painted where the real value is 87% — a lying number, the one
    // thing a BI surface must never show). Hidden document ⇒ jump
    // straight to the target; if the document hides DURING a tween,
    // snap immediately. Nobody is watching — there is nothing to
    // animate.
    if (
      reduced ||
      suppressed ||
      typeof requestAnimationFrame !== "function" ||
      (typeof document !== "undefined" && document.visibilityState === "hidden")
    ) {
      setDisplay(target);
      return undefined;
    }
    const from = displayRef.current;
    if (from === target) return undefined;
    let start: number | null = null;
    const step = (ts: number): void => {
      if (start === null) start = ts;
      const t = Math.min((ts - start) / durMs, 1);
      const v = from + (target - from) * easeOutCubic(t);
      setDisplay(t >= 1 ? target : v);
      if (t < 1) rafRef.current = requestAnimationFrame(step);
    };
    // `holdMs` holds the current display before the count starts, so
    // the ENTRANCE lands on the same beat as the CSS entrances
    // (MOTION.delayEntrance). Zero delay starts on the next frame.
    let delayTimer: ReturnType<typeof setTimeout> | null = null;
    if (holdMs > 0) {
      delayTimer = setTimeout(() => {
        rafRef.current = requestAnimationFrame(step);
      }, holdMs);
    } else {
      rafRef.current = requestAnimationFrame(step);
    }
    const onVisibility = (): void => {
      if (document.visibilityState === "hidden") {
        if (delayTimer !== null) clearTimeout(delayTimer);
        if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
        setDisplay(target);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    // Backstop for OCCLUDED-but-"visible" windows (covered by another
    // window): rAF pauses without a visibilitychange event, freezing
    // the tween mid-count. A throttled timeout still fires and snaps
    // to the target — harmless no-op when the tween already landed.
    // Sized to THIS phase's beat (entrance hold + duration, or the bare
    // morph), so shortening a refetch does not leave a long dead window.
    const backstop = setTimeout(() => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      setDisplay(target);
    }, holdMs + durMs + 500);
    return () => {
      if (delayTimer !== null) clearTimeout(delayTimer);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      document.removeEventListener("visibilitychange", onVisibility);
      clearTimeout(backstop);
    };
  }, [numeric, value, reduced, suppressed, durationMs, delayMs]);

  // Decimal policy for the frames IN FLIGHT (review #56 #9): frames
  // quantize to the target's decimal count so `fmt` sees values with a
  // consistent precision for the whole tween — no width dance
  // mid-count. The default frame formatter is memoized per (target
  // decimals): `Intl.NumberFormat` construction is expensive and this
  // runs every animation frame (custom formatters get the same
  // treatment via the module-level cache in `formatting.ts`).
  const targetDecimals = numeric ? decimalsOf(value as number) : 0;
  const defaultFrameFmt = useMemo(() => {
    const nf = new Intl.NumberFormat(undefined, {
      minimumFractionDigits: targetDecimals,
      maximumFractionDigits: targetDecimals,
    });
    return (n: number): string => nf.format(n);
  }, [targetDecimals]);

  if (!numeric) {
    // Documented passthrough (see module docstring) — never animated.
    return <span className={className}>{value}</span>;
  }

  // The LANDED frame formats `value` EXACTLY as received — no
  // quantization (motion audit hallazgo 2). This is the settled state:
  // rounding it here is not a transient artifact, it is a wrong number
  // on screen, and it is the ONLY frame reduced-motion users, hidden
  // tabs and FadeSwap ghosts ever see. It also re-enables the
  // byte-identical shortcut of pre-formatted values: `parsed.render`
  // returns the ORIGINAL string literally when it receives the exact
  // `value` (a pre-rounded one never matched).
  const landed = display === (value as number);
  const text = landed
    ? format
      ? format(value as number)
      : formatExact(value as number)
    : (format ?? defaultFrameFmt)(Number(display.toFixed(targetDecimals)));

  return (
    <span
      className={className}
      style={{ fontVariantNumeric: "tabular-nums" }}
      data-pm-number-tween
    >
      {text}
    </span>
  );
}
